import { Pencil } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { AutosaveIndicator, type AutosaveState } from '@/components/patterns/AutosaveIndicator'
import { RichTextEditor } from '@/components/RichTextEditor'
import { RichTextView } from '@/components/RichTextView'
import { Button } from '@/components/ui/button'
import { editedLine } from '../record/format'

const AUTOSAVE_MS = 1500

function isBlank(html: string): boolean {
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() === ''
}

function readDraft(key: string): string | null {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function writeDraft(key: string, value: string | null) {
  try {
    if (value === null) sessionStorage.removeItem(key)
    else sessionStorage.setItem(key, value)
  } catch {
    // Without storage the draft only lives as long as the page.
  }
}

/**
 * "Over <naam>" (design §3.5/§4.4, ED-20/21): rich text with highlighter via the
 * spec-58 editor. Click "Bewerken"; changes save on their own after a short pause
 * (autosave indicator, IA-13) and on "Klaar"; the draft survives a reload
 * (sessionStorage, IA-15) and a page that finds one opens straight in the editor, so
 * the text that was typed is not hidden behind the saved version. An empty summary is
 * saved as null. `edited` adds the line "Bewerkt 13 okt · Jesse" under the title.
 */
export function SummaryEditor({
  title,
  html,
  placeholder,
  draftKey,
  edited,
  onSave,
}: {
  title: string
  html: string | null
  placeholder: string
  draftKey: string
  /** Who last changed the text, and when (`summary_updated_at` / `summary_updated_by`). */
  edited?: { at: string; by: string | null } | null
  onSave: (html: string | null) => Promise<void>
}) {
  // null = not editing; otherwise the text in the editor.
  const [draft, setDraft] = useState<string | null>(() => readDraft(draftKey))
  const [state, setState] = useState<AutosaveState>('idle')
  const [reason, setReason] = useState<string | null>(null)
  const editing = draft !== null
  const timer = useRef<number | undefined>(undefined)
  // The text in the editor right now: a save reports for the text it was started with,
  // and what was typed since is still unsaved.
  const latest = useRef(draft ?? '')
  // A draft found in storage was never saved.
  const dirty = useRef(draft !== null)
  // Numbers the saves: only the newest one reports its outcome.
  const saves = useRef(0)
  const box = useRef<HTMLDivElement>(null)
  const editButton = useRef<HTMLButtonElement>(null)
  // Where the focus goes once the editing state has changed: set by the buttons, so a
  // draft that is restored on load does not take the focus away from the page.
  const focusNext = useRef<'editor' | 'button' | null>(null)

  useEffect(() => () => window.clearTimeout(timer.current), [])

  useEffect(() => {
    const wanted = focusNext.current
    focusNext.current = null
    if (wanted === 'editor' && editing) {
      box.current?.querySelector<HTMLElement>('[role="textbox"], textarea')?.focus()
    } else if (wanted === 'button' && !editing) {
      editButton.current?.focus()
    }
  }, [editing])

  function start() {
    const kept = readDraft(draftKey)
    const text = kept ?? html ?? ''
    latest.current = text
    dirty.current = kept !== null
    focusNext.current = 'editor'
    setState('idle')
    setDraft(text)
  }

  async function persist(value: string): Promise<boolean> {
    saves.current += 1
    const mine = saves.current
    setState('saving')
    try {
      await onSave(isBlank(value) ? null : value)
      if (latest.current === value) {
        dirty.current = false
        writeDraft(draftKey, null)
      }
      // Text typed during the save has its own save coming: nothing is "saved" yet.
      if (mine === saves.current) setState(latest.current === value ? 'saved' : 'idle')
      return true
    } catch (caught) {
      if (mine === saves.current) {
        setReason(caught instanceof Error ? caught.message : null)
        setState('error')
      }
      return false
    }
  }

  function change(value: string) {
    latest.current = value
    dirty.current = true
    setDraft(value)
    writeDraft(draftKey, value)
    setState((current) => (current === 'saved' ? 'idle' : current))
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => void persist(value), AUTOSAVE_MS)
  }

  async function finish() {
    window.clearTimeout(timer.current)
    const value = latest.current
    // Unchanged, or already saved by the autosave: there is nothing to send.
    const saved = !dirty.current || (await persist(value))
    // Typing went on while it saved: its own save is on the way, so stay in the editor.
    if (saved && latest.current === value) {
      focusNext.current = 'button'
      setDraft(null)
    }
  }

  return (
    <section aria-label={title} className="vonk-card flex flex-col gap-3 p-5">
      <header className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 flex-col">
          <h2 className="font-heading text-base font-semibold">{title}</h2>
          {edited ? <p className="text-xs text-muted-foreground">{editedLine(edited.at, edited.by)}</p> : null}
        </div>
        <div className="flex items-center gap-2">
          <AutosaveIndicator
            state={state}
            message={reason}
            onRetry={() => void persist(latest.current)}
          />
          {editing ? (
            <Button type="button" size="sm" onClick={() => void finish()}>
              Klaar
            </Button>
          ) : (
            <Button ref={editButton} type="button" size="sm" variant="ghost" onClick={start}>
              <Pencil />
              Bewerken
            </Button>
          )}
        </div>
      </header>
      {editing ? (
        <div ref={box}>
          <RichTextEditor
            format="html"
            value={draft}
            onChange={(value) => change(value)}
            ariaLabel={title}
            placeholder={placeholder}
          />
        </div>
      ) : html ? (
        <RichTextView html={html} />
      ) : (
        <button type="button" onClick={start} className="text-left text-sm text-muted-foreground">
          {placeholder}
        </button>
      )}
    </section>
  )
}
